import { TranslatedString } from "@web-monorepo/translation-runtime";
import { Lesson, Slide } from "src/pods/ideas/data/types";
import { displayClose, displayOpen } from "src/pods/realtime";
import DefaultAppModal from "src/shared/components/AppModal";
import DefaultPhoneWatchVideo from "./PhoneWatchVideo";
import {
  useWatchVideoContainer,
  type UseWatchVideoContainerProps,
  type UseWatchVideoContainerResult,
} from "./useWatchVideoContainer.vm";
import DefaultWatchVideo from "./WatchVideo";
import styles from "./WatchVideoContainer.module.css";

type WatchVideoContainerProps = {
  displayOpen: typeof displayOpen;
  displayClose: typeof displayClose;
  lesson: Lesson;
  watch: Slide;
  onBack: VoidFunction;
  /**
   * DI hooks for storybook stories — production callers leave undefined.
   *   - `useVm`: supplies canned cast / playback state without touching the
   *     realtime + ideas stores or the action dispatcher.
   *   - `AppModal` / `PhoneWatchVideo` / `WatchVideo`: stubs that sidestep
   *     RadiantMP and the modal portal machinery.
   */
  di?: {
    useVm?: (props: UseWatchVideoContainerProps) => UseWatchVideoContainerResult;
    AppModal?: React.ComponentType<React.ComponentProps<typeof DefaultAppModal>>;
    PhoneWatchVideo?: React.ComponentType<React.ComponentProps<typeof DefaultPhoneWatchVideo>>;
    WatchVideo?: React.ComponentType<React.ComponentProps<typeof DefaultWatchVideo>>;
  };
};

export default function WatchVideoContainer({
  displayOpen,
  displayClose,
  lesson,
  watch,
  onBack,
  di,
}: WatchVideoContainerProps) {
  const useVm = di?.useVm ?? useWatchVideoContainer;
  const AppModal = di?.AppModal ?? DefaultAppModal;
  const PhoneWatchVideo = di?.PhoneWatchVideo ?? DefaultPhoneWatchVideo;
  const WatchVideo = di?.WatchVideo ?? DefaultWatchVideo;

  const { shouldPlayOnPhone, isCasting, isConnected, isPlaying, handlers } = useVm({
    displayOpen,
    displayClose,
    watch,
    onBack,
  });

  return (
    <article className={styles.app}>
      <AppModal
        title={lesson.title as TranslatedString}
        onRequestStart={handlers.onRequestStart}
        onRequestStop={handlers.onRequestStop}
        onRequestHide={handlers.onRequestHide}
        className={styles.modal}
      >
        {(shouldPlayOnPhone || !isCasting || !isConnected) && (
          <PhoneWatchVideo watch={watch} canPlayOnComputer={isConnected} onPlayOnComputer={handlers.onPlayOnComputer} />
        )}
        {!shouldPlayOnPhone && isCasting && isConnected && (
          <WatchVideo
            isPlaying={isPlaying}
            onPlayOnPhone={handlers.onPlayOnPhone}
            onTogglePlay={handlers.onTogglePlay}
            onForward={handlers.onForward}
            onReplay={handlers.onReplay}
          />
        )}
      </AppModal>
    </article>
  );
}
